import React, { useState } from 'react';
import { Download, TrendingUp, TrendingDown, Clock, CheckCircle, XCircle } from 'lucide-react';

const AnalyticsDashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('Last 6 Months');
  const [selectedMetrics, setSelectedMetrics] = useState('All Metrics');
  const [activeTab, setActiveTab] = useState('Overview');

  // Mock data for the charts
  const responseTimeData = [
    { month: 'Jan', time: 8.5 },
    { month: 'Feb', time: 8.0 },
    { month: 'Mar', time: 9.5 },
    { month: 'Apr', time: 7.5 },
    { month: 'May', time: 9.0 },
    { month: 'Jun', time: 7.5 },
  ];

  const ambulanceTypeData = [
    { type: 'Basic Life Support', percentage: 48, color: '#8B5CF6' },
    { type: 'Advanced Life Support', percentage: 32, color: '#10B981' },
    { type: 'Critical Care', percentage: 20, color: '#F59E0B' },
  ];

  const tabs = ['Overview', 'Performance', 'Utilization', 'Financial'];

  // Hospital Performance Data
  const hospitalCards = [
    {
      title: "Active Requests",
      subtitle: "In-progress or assigned",
      value: "9",
      icon: Clock,
      color: "text-blue-600"
    },
    {
      title: "Completed Today",
      subtitle: "Trips completed",
      value: "34",
      icon: CheckCircle,
      color: "text-green-600"
    },
    {
      title: "Avg. ETA",
      subtitle: "From dispatch to pickup",
      value: "12m",
      icon: TrendingUp,
      color: "text-purple-600"
    },
    {
      title: "Cancellations",
      subtitle: "Today",
      value: "2",
      icon: XCircle,
      color: "text-red-600"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                Analytics & Reports
              </h1>
              <p className="text-gray-600">
                Fleet usage, response times, trip history, and revenue insights.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Filters */}
              <div className="flex gap-3">
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>Last 6 Months</option>
                  <option>Last 3 Months</option>
                  <option>Last Year</option>
                </select>
                
                <select
                  value={selectedMetrics}
                  onChange={(e) => setSelectedMetrics(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>All Metrics</option>
                  <option>Response Times</option>
                  <option>Fleet Utilization</option>
                  <option>Revenue</option>
                </select>
              </div>
              
              {/* Export Button */}
              <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <Download className="w-4 h-4" />
                Export Report
              </button>
            </div>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Total Ambulance Trips */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Total Ambulance Trips</h3>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl lg:text-3xl font-bold text-gray-900">2,847</span>
              <span className="text-sm font-medium text-green-600">+12.5%</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">This month</p>
          </div>

          {/* Average Response Time */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Average Response Time</h3>
              <TrendingDown className="w-5 h-5 text-green-500" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl lg:text-3xl font-bold text-gray-900">7.8 mins</span>
              <span className="text-sm font-medium text-green-600">-0.5 mins</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">From last month</p>
          </div>

          {/* Fleet Utilization */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Fleet Utilization</h3>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl lg:text-3xl font-bold text-gray-900">78.5%</span>
              <span className="text-sm font-medium text-green-600">+5.2%</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Current month</p>
          </div>

          {/* Revenue Generated */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Revenue Generated</h3>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl lg:text-3xl font-bold text-gray-900">₹1,82,000</span>
              <span className="text-sm font-medium text-green-600">+18.3%</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">This month</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content Section */}
        {activeTab === 'Overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Response Time Trends Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Response Time Trends
                </h3>
                <p className="text-sm text-gray-600">
                  Average ambulance response time over the last 6 months
                </p>
              </div>
              
              <div className="relative h-64">
                <svg className="w-full h-full" viewBox="0 0 400 200">
                  {/* Y-axis labels */}
                  <text x="10" y="20" className="text-xs fill-gray-500">12</text>
                  <text x="10" y="50" className="text-xs fill-gray-500">9</text>
                  <text x="10" y="80" className="text-xs fill-gray-500">6</text>
                  <text x="10" y="110" className="text-xs fill-gray-500">3</text>
                  <text x="10" y="140" className="text-xs fill-gray-500">0</text>
                  
                  {/* X-axis labels */}
                  {responseTimeData.map((data, index) => (
                    <text
                      key={data.month}
                      x={60 + index * 55}
                      y="190"
                      className="text-xs fill-gray-500"
                      textAnchor="middle"
                    >
                      {data.month}
                    </text>
                  ))}
                  
                  {/* Target line */}
                  <line
                    x1="50"
                    y1="40"
                    x2="350"
                    y2="40"
                    stroke="#10B981"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                  
                  {/* Data line */}
                  <polyline
                    points={responseTimeData.map((data, index) => 
                      `${60 + index * 55},${200 - (data.time * 15)}`
                    ).join(' ')}
                    fill="none"
                    stroke="#8B5CF6"
                    strokeWidth="3"
                  />
                  
                  {/* Data points */}
                  {responseTimeData.map((data, index) => (
                    <circle
                      key={data.month}
                      cx={60 + index * 55}
                      cy={200 - (data.time * 15)}
                      r="4"
                      fill="#8B5CF6"
                    />
                  ))}
                </svg>
              </div>
            </div>

            {/* Ambulance Type Distribution Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Ambulance Type Distribution
                </h3>
                <p className="text-sm text-gray-600">
                  Distribution of ambulance types used
                </p>
              </div>
              
              <div className="flex flex-col lg:flex-row items-center gap-6">
                {/* Pie Chart */}
                <div className="relative w-48 h-48">
                  <svg className="w-full h-full" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#E5E7EB"
                      strokeWidth="8"
                    />
                    
                    {ambulanceTypeData.map((data, index) => {
                      const previousPercentages = ambulanceTypeData
                        .slice(0, index)
                        .reduce((sum, item) => sum + item.percentage, 0);
                      const startAngle = (previousPercentages / 100) * 360;
                      const endAngle = ((previousPercentages + data.percentage) / 100) * 360;
                      
                      const x1 = 50 + 40 * Math.cos((startAngle - 90) * Math.PI / 180);
                      const y1 = 50 + 40 * Math.sin((startAngle - 90) * Math.PI / 180);
                      const x2 = 50 + 40 * Math.cos((endAngle - 90) * Math.PI / 180);
                      const y2 = 50 + 40 * Math.sin((endAngle - 90) * Math.PI / 180);
                      
                      const largeArcFlag = data.percentage > 50 ? 1 : 0;
                      
                      return (
                        <path
                          key={data.type}
                          d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                          fill={data.color}
                        />
                      );
                    })}
                  </svg>
                </div>
                
                {/* Legend */}
                <div className="space-y-3">
                  {ambulanceTypeData.map((data) => (
                    <div key={data.type} className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: data.color }}
                      />
                      <span className="text-sm text-gray-700">{data.type}</span>
                      <span className="text-sm font-medium text-gray-900">
                        {data.percentage}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Performance Tab - Hospital Dashboard Content */}
        {activeTab === 'Performance' && (
          <div className="space-y-6">
            {/* Hospital Performance Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {hospitalCards.map((card, index) => {
                const Icon = card.icon;
                return (
                  <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-medium text-gray-900">
                        {card.title}
                      </h3>
                      <Icon className={`w-5 h-5 ${card.color}`} />
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-600">
                        {card.subtitle}
                      </p>
                      <p className="text-2xl lg:text-3xl font-bold text-gray-900">
                        {card.value}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Hospital Performance Summary */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Hospital Performance Summary
                </h3>
                <p className="text-sm text-gray-600">
                  Key metrics and performance indicators for hospital operations
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Response Metrics</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Average Response Time</span>
                      <span className="text-sm font-medium text-gray-900">8.2 mins</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Emergency Response</span>
                      <span className="text-sm font-medium text-gray-900">5.1 mins</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Non-Emergency</span>
                      <span className="text-sm font-medium text-gray-900">12.3 mins</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Service Quality</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Patient Satisfaction</span>
                      <span className="text-sm font-medium text-gray-900">94.2%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">On-Time Arrival</span>
                      <span className="text-sm font-medium text-gray-900">87.5%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Service Rating</span>
                      <span className="text-sm font-medium text-gray-900">4.6/5.0</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Operational Efficiency</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Fleet Utilization</span>
                      <span className="text-sm font-medium text-gray-900">76.8%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Driver Efficiency</span>
                      <span className="text-sm font-medium text-gray-900">89.3%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Cost per Trip</span>
                      <span className="text-sm font-medium text-gray-900">₹2,450</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Utilization Tab */}
        {activeTab === 'Utilization' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Fleet Utilization Analysis
              </h3>
              <p className="text-sm text-gray-600">
                Detailed breakdown of ambulance fleet usage and efficiency metrics
              </p>
            </div>
            
            <div className="flex items-center justify-center h-64 text-gray-500 border-2 border-dashed rounded-lg">
              <div className="text-center">
                <p className="text-lg font-medium">Fleet Utilization Dashboard</p>
                <p className="text-sm">Detailed utilization metrics and analysis</p>
              </div>
            </div>
          </div>
        )}

        {/* Financial Tab */}
        {activeTab === 'Financial' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Financial Performance
              </h3>
              <p className="text-sm text-gray-600">
                Revenue analysis, cost breakdown, and financial metrics
              </p>
            </div>
            
            <div className="flex items-center justify-center h-64 text-gray-500 border-2 border-dashed rounded-lg">
              <div className="text-center">
                <p className="text-lg font-medium">Financial Dashboard</p>
                <p className="text-sm">Revenue, costs, and financial performance metrics</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
